import React, { useEffect, useRef } from 'react';
import nouislider from 'nouislider';
import { useDataContext } from '../context/DataContext';

const Filter = () => {
  const {
    isLoading,
    availableFilters,
    initialActiveFilters,
    activeFilters,
    setActiveFilters,
    showFilters,
    setShowFilters,
    filteredConstructionNumbers,
    housingTypes,
    activeFiltersCount
  } = useDataContext();
  const priceSliderRef = useRef(null);
  const priceMinInputRef = useRef(null);
  const priceMaxInputRef = useRef(null);

  useEffect(() => {
    if (showFilters) {
      document.body.classList.add('no-scroll--lt-desk');
    } else {
      document.body.classList.remove('no-scroll--lt-desk');
    }
    return () => {
      document.body.classList.remove('no-scroll--lt-desk');
    };
  }, [showFilters]);

  const formatPrice = (ref, value) => {
    ref.current.value = new Intl.NumberFormat('nl-NL', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  };

  const unformatPrice = (ref, value) => {
    ref.current.value = +value;
  };

  const handleChange = (event) => {
    const {type, checked, name, value} = event.target;

    if (type === 'checkbox') {
      setActiveFilters((prevState) => ({
        ...prevState,
        [name]: (!checked) ? prevState[name].filter((item) => item !== value) : [...prevState[name], value]
      }));
    } else {
      setActiveFilters((prevState) => ({
        ...prevState,
        [name]: +value
      }));
    }

    formatPrice(priceMinInputRef, activeFilters.priceMin);
    formatPrice(priceMaxInputRef, activeFilters.priceMax);
  };

  useEffect(() => {
    if (!isLoading && priceSliderRef.current) {
      nouislider.create(priceSliderRef.current, {
        start: [0, 0],
        connect: true,
        range: {
          min: 0,
          max: 0
        },
        step: 25000
      });

      priceSliderRef.current.noUiSlider.on('update', (values) => {
        formatPrice(priceMinInputRef, values[0]);
        formatPrice(priceMaxInputRef, values[1]);
        setActiveFilters((prevState) => ({
          ...prevState,
          priceMin: +values[0],
          priceMax: +values[1]
        }));
      });
    }
  }, [priceSliderRef, setActiveFilters, isLoading]);

  useEffect(() => {
    if (!isLoading && priceSliderRef.current && activeFilters.priceMin > 0 && activeFilters.priceMax >= activeFilters.priceMin) {
      priceSliderRef.current.noUiSlider.updateOptions({
        start: [availableFilters.priceMin, availableFilters.priceMax],
        range: {
          min: +availableFilters.priceMin,
          max: +availableFilters.priceMax
        }
      }, true);
    }
  }, [availableFilters.priceMin, availableFilters.priceMax, isLoading, activeFilters.priceMin, activeFilters.priceMax]);

  useEffect(() => {
    if (!isLoading && priceSliderRef.current) {
      priceSliderRef.current.noUiSlider.set([activeFilters.priceMin, activeFilters.priceMax]);
    }
  }, [activeFilters.priceMin, activeFilters.priceMax, isLoading]);

  return (
    <div className={`housing-types-filter ${showFilters ? '' : 'visuallyhidden--lt-desk'}`}>
      <button type="button" className="button-link visuallyhidden--desk button--right" onClick={() => setShowFilters(false)}>
        <svg role="img" className="icon">
          <use xlinkHref="/public/img/sprite.svg#close" />
        </svg>
      </button>
      <h2 className="heading-s heading--stripe sign">Filters</h2>
      <div className="housing-types-filter__active-filters sign">
        {activeFiltersCount > 0 && (
          <div className="grid grid--middle">
            <div className="grid__cell unit-6-12 text-small">
              {activeFiltersCount} filter{activeFiltersCount !== 1 && 's'} actief
            </div>
            <div className="grid__cell unit-6-12 text-right">
              {activeFiltersCount > 0 && <button type="button" className="button-link" onClick={() => setActiveFilters(initialActiveFilters)}>Reset filters</button>}
            </div>
          </div>
        )}

        {Object.keys(activeFilters).map((key) => {
          if ((key === 'priceMin' && activeFilters[key] === initialActiveFilters[key])
            || (key === 'priceMax' && activeFilters[key] === initialActiveFilters[key])
            || !activeFilters[key]) {
            return null;
          }

          if (Array.isArray(activeFilters[key])) {
            return activeFilters[key].map((value) => (
              <label key={`${key}-${value}`} className="housing-types-filter__active-filters__item">
                <input
                  type="checkbox"
                  name={key}
                  value={value}
                  onChange={handleChange}
                  checked
                />
                {key === 'housingTypes' ? housingTypes.find((type) => type.id === value).houseCluster : value}
                <svg role="img" className="icon">
                  <use xlinkHref="/public/img/sprite.svg#close" />
                </svg>
              </label>
            ));
          }

          return (
            <button
              key={key}
              type="button"
              name={key}
              className="housing-types-filter__active-filters__item"
              value={initialActiveFilters[key]}
              onClick={handleChange}
            >
              {['priceMin', 'priceMax'].includes(key) ? (
                <>
                  {key === 'priceMin' ? 'Min: ' : 'Max: '}
                  {new Intl.NumberFormat('nl-NL', {
                    style: 'currency',
                    currency: 'EUR',
                    minimumFractionDigits: 0,
                    maximumFractionDigits: 0
                  }).format(activeFilters[key])}
                </>
              ) : (
                <>
                  {activeFilters[key]}
                  {['metricsLiving', 'metricsLot'].includes(key) && <> m<sup>2</sup></>}
                </>
              )}
              <svg role="img" className="icon">
                <use xlinkHref="/public/img/sprite.svg#close" />
              </svg>
            </button>
          );
        })}
      </div>

      {isLoading ? (
        <span className="housing-type-skeleton" style={{width: '100vw', maxWidth: '100%', height: '400px'}} />
      ) : (
        <>
          <h5 className="housing-types-filter__title">Prijs range</h5>
          <div className="nouislider">
            <div className="grid sign-s">
              <div className="grid__cell unit-6-12">
                <input
                  type="text"
                  name="priceMin"
                  onBlur={handleChange}
                  onFocus={() => unformatPrice(priceMinInputRef, activeFilters.priceMin)}
                  ref={priceMinInputRef}
                />
              </div>
              <div className="grid__cell unit-6-12">
                <input
                  type="text"
                  name="priceMax"
                  onBlur={handleChange}
                  onFocus={() => unformatPrice(priceMaxInputRef, activeFilters.priceMax)}
                  ref={priceMaxInputRef}
                />
              </div>
            </div>
            <div ref={priceSliderRef} />
          </div>

          {availableFilters.state && (
            <div className="sign">
              <h5 className="housing-types-filter__title">Status</h5>
              <div>
                {availableFilters.state.map((item) => (
                  <div key={item}>
                    <label className="custom-checkbox">
                      <input
                        type="checkbox"
                        name="state"
                        className="custom-checkbox__input"
                        value={item}
                        onChange={handleChange}
                        checked={activeFilters.state.includes(item)}
                      />
                      {item}
                      <div className="custom-checkbox__element" />
                    </label>
                  </div>
                ))}
              </div>
            </div>
          )}

          {availableFilters.housingKinds && (
            <div className="sign">
              <h5 className="housing-types-filter__title">Soort woning</h5>
              <div>
                {availableFilters.housingKinds.map((item) => (
                  <div key={item}>
                    <label className="custom-checkbox">
                      <input
                        type="checkbox"
                        name="housingKinds"
                        className="custom-checkbox__input"
                        value={item}
                        onChange={handleChange}
                        checked={activeFilters.housingKinds.includes(item)}
                      />
                      {item}
                      <div className="custom-checkbox__element" />
                    </label>
                  </div>
                ))}
              </div>
            </div>
          )}

          {availableFilters.metricsLiving && (
            <div className="sign">
              <h5 className="housing-types-filter__title">Gebruiksoppervlakte</h5>
              <div>
                {availableFilters.metricsLiving.map((item) => (
                  <div key={item}>
                    <label className="custom-radio">
                      <input
                        type="radio"
                        name="metricsLiving"
                        className="custom-radio__input"
                        value={item}
                        checked={+activeFilters.metricsLiving === item}
                        onChange={handleChange}
                      />
                      {item} m<sup>2</sup> of meer
                      <div className="custom-radio__element" />
                    </label>
                  </div>
                ))}
              </div>
            </div>
          )}

          {availableFilters.metricsLot && (
            <div className="sign">
              <h5 className="housing-types-filter__title">Perceeloppervlakte</h5>
              <div>
                {availableFilters.metricsLot.map((item) => (
                  <div key={item}>
                    <label className="custom-radio">
                      <input
                        type="radio"
                        name="metricsLot"
                        className="custom-radio__input"
                        value={item}
                        onChange={handleChange}
                        checked={+activeFilters.metricsLot === item}
                      />
                      {item} m<sup>2</sup> of meer
                      <div className="custom-radio__element" />
                    </label>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}
      <div className="housing-types-filter__apply text-center visuallyhidden--desk">
        <button type="button" className="button button--full" onClick={() => setShowFilters(false)}>Toon {filteredConstructionNumbers.length} resultaten</button>
      </div>
    </div>
  );
};

export default Filter;
